import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import * as dayjs from 'dayjs';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ChangedEvent, LinkChanged, RoutersEventsAPI } from '../api/core';
import { AppConfig, AppState, APP_CONFIG } from '../app.models';
import * as fromDevices from '../devices';
import { SseService } from '../shared/services/sse.service';
import * as fromTenants from '../tenants';
import { loadTenants } from '../tenants';
import { ExtendedTenantDto } from '../tenants/tenants.models';
import * as UserActions from '../users/users.actions';
import { LoggedInUser, UserRoles } from '../users/users.models';
import * as userSelectors from '../users/users.selectors';
import { DialogWidths } from '../utils';
import { LanguageDialogComponent } from './dialogs/language-dialog/language-dialog.component';
import { Im2mMarker } from '@m2mgate/sage-ui/maps';
import { environment } from '../../environments/environment';

@Component({
    selector: 'liebherr-base',
    templateUrl: 'base.component.html',
    styleUrls: ['base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseComponent extends SubscriptionComponent implements OnInit {
    UserRoles = UserRoles;

    language$ = this.store.select(userSelectors.selectLanguage);
    loggedInUser$: Observable<LoggedInUser | undefined> = this.store.select(userSelectors.selectLoggedInUser);
    tenants$: Observable<ExtendedTenantDto[] | undefined> = this.store.select(fromTenants.selectTenants);

    loggedInUserTenantName$: Observable<string>;

    markers = this.getMarkers(10);
    constructor(
        private oauthService: OAuthService,
        public dialog: MatDialog,
        private store: Store<AppState>,
        private router: Router,
        private translateService: TranslocoService,
        private sseService: SseService,
        @Inject(APP_CONFIG) private appConfig: AppConfig
    ) {
        super();

        this.loggedInUserTenantName$ = combineLatest([this.loggedInUser$, this.tenants$]).pipe(
            filter(([loggedInUser, tenants]) => (tenants ? tenants?.length > 0 : false)),
            map(([loggedInUser, tenants]) => {
                return tenants?.find((t) => t.keycloakId === loggedInUser?.tenantId)?.name || '';
            })
        );
    }

    ngOnInit() {
        // load all tenants
        this.store.dispatch(loadTenants());
        // listen to language changes
        this.sub.add(
            this.language$.subscribe((lang) => {
                this.translateService.setActiveLang(lang || 'en');
                dayjs.locale(lang);
            })
        );

        if (!environment.e2e) {
            // listen to server sent events and update devices, not needed in e2e
            this.sub.add(
                this.sseService
                    .open<ChangedEvent>(this.appConfig.coreUrl + RoutersEventsAPI.GetStateChangedEventsPath)
                    .subscribe((event) => {
                        this.store.dispatch(fromDevices.updateDevice({ event }));
                    })
            );
            this.sub.add(
                this.sseService.open<LinkChanged>(this.appConfig.coreUrl + RoutersEventsAPI.GetLinkChangedEventsPath).subscribe((event) => {
                    this.store.dispatch(fromDevices.updateLink({ event }));
                })
            );
        }
    }

    openLanguageDlg(loggedInUser?: LoggedInUser) {
        const dialogRef = this.dialog.open<LanguageDialogComponent>(LanguageDialogComponent, { width: DialogWidths.SMALL });
        dialogRef.afterClosed().subscribe((res) => {
            // console.log('dialog closed', res, loggedInUser);
            if (res && loggedInUser) {
                this.store.dispatch(
                    UserActions.updateUserLanguage({
                        user: { ...loggedInUser, languageCode: res },
                    })
                );
            }
        });
    }

    onLogout() {
        this.oauthService.logOut();
    }
    /**
     * @internal
     */
    getMarkers(count: number) {
        const markers: Im2mMarker[] = [];

        for (let i = 0; i < count; i++) {
            const latLng = this.randomLatLng();
            markers.push({
                id: (i + 1).toString(),
                ...latLng,
            });
        }

        return markers;
    }

    randomLatLng() {
        const latBounds = [59.60109549032134, 50.80078125];
        const lngBounds = [42.00032514831623, -33.35449218750001];

        const lat = Math.random() * (latBounds[1] - latBounds[0] + 1) + latBounds[0];
        const lng = Math.random() * (lngBounds[1] - lngBounds[0] + 1) + lngBounds[0];
        return { lat, lng };
    }
}
